import * as React from "react"

import MedicSection from "./MedicSection"
import MedicVerdictHeader from "./MedicVerdictHeader"
import { BUCKET_NAMES } from "./types"
import type { MedicResult } from "./types"

/**
 * The Feed Medic report body, rendered identically by the in-app panel and the
 * public share page. Takes only the result payload — no Redux, no router, no
 * session — so both surfaces stay a single renderer.
 *
 * Callers must place this inside a `.tw-root` element: Tailwind's global
 * preflight is deliberately excluded from this app in favour of a preflight
 * scoped to that class (see ui/src/tw/tw.css), so outside it the component
 * inherits the legacy stylesheet's resets and spacing collapses.
 */
export default function MedicReport({
  result,
  renderRanAt,
  runState,
}: {
  result?: MedicResult
  renderRanAt?: () => React.ReactNode
  /* Forwarded to the summary header; see its own note for when to set it. */
  runState?: 'queued' | 'running'
}) {
  if (!result) return null

  /*
   * `verdict` is only written when the run reaches a terminal state, so its
   * presence is what distinguishes a section that hasn't run yet (pending) from
   * one that never will (skipped).
   */
  const runTerminal = Boolean(result.verdict)

  return (
    <div className="tw:flex tw:flex-col tw:gap-3">
      <MedicVerdictHeader result={result} renderRanAt={renderRanAt} runState={runState} />
      {BUCKET_NAMES.map((name) => (
        <MedicSection key={name} name={name} bucket={result[name]} runTerminal={runTerminal} />
      ))}
    </div>
  )
}
